/* eslint-disable camelcase */
import { FormikProps } from "formik";
import { last } from "lodash-es";
import React from "react";
import { useIntl } from "react-intl";
import { apiShape, withApi } from "../../context";
import ProductRules from "../../rules/ProductRules";
import { couldBeAnEndressHauserSerialNumber, extractCode } from "../../utils";
import { MeasuringIntervalOptions } from "../../types";

type FormValues = {
  tenant?: {
    id?: number;
    name?: string;
    public?: boolean;
  };
  id?: number;
  manufacturer?: {
    id?: number;
    name?: string;
    new?: boolean;
    tenantId?: number;
    tenantPublic?: boolean;
    gpsActive?: boolean;
  };
  gpsInterval?: {
    id?: string;
    name?: string;
  };
  serial_number?: string;
  product?: {
    manufacturer?: {
      id?: number;
    };
    new?: boolean;
    code?: string;
    manufacturerId?: number;
    tenantId?: number;
    tenantPublic?: boolean;
    tenantName?: string;
  };
  calibration_time?: $TSFixMe; // TODO: PropTypes.instanceOf(Date)
  measuringInterval?: MeasuringIntervalOptions;
  transmissionInterval?: {
    id?: string;
    name?: string;
  };
  firstMeasurement?: $TSFixMe; // TODO: PropTypes.instanceOf(Date)
  gpsActive?: boolean;
  orderCode?: string;
};

type OwnProps = {
  api: apiShape;
  tenant?: string;
  userAdminTenants?: $TSFixMe;
  gpsEnabled?: boolean;
  skipFirstProductLookup?: boolean;
  showMeasuringInterval?: boolean;
  showTimeOfFirstMeasurement?: boolean;
  showTransmissionInterval?: boolean;
  gpsIntervalOptions?: {
    id?: string;
    name?: string;
    gpsOptions?: {
      id?: string;
      name?: string;
    }[];
  }[];
  loadGPSErrors?: $TSFixMeFunction;
  gpsError: string | undefined | null;
} & FormikProps<FormValues>;

const defaultProps: Partial<OwnProps> = {
  userAdminTenants: undefined,
  showMeasuringInterval: false,
  showTimeOfFirstMeasurement: false,
  showTransmissionInterval: false,
  skipFirstProductLookup: false,
};

type Props = OwnProps & typeof defaultProps;

export function AssetFormSynchronization(props: Props) {
  const {
    api,
    values,
    setFieldValue,
    userAdminTenants,
    showMeasuringInterval,
    showTimeOfFirstMeasurement,
    showTransmissionInterval,
    gpsIntervalOptions,
    setFieldTouched,
    loadGPSErrors,
    gpsEnabled,
    skipFirstProductLookup,
    gpsError,
    validateForm,
  } = props;
  const {
    manufacturer,
    tenant,
    serial_number,
    product,
    measuringInterval,
    transmissionInterval,
    firstMeasurement,
    gpsInterval,
    gpsActive,
    orderCode,
  } = values;

  const intl = useIntl();

  const productRules = ProductRules.find(
    manufacturer?.name,
    product?.code,
    manufacturer?.tenantPublic,
  );

  const isNotEndress =
    manufacturer &&
    (!manufacturer.tenantPublic || manufacturer.name !== "Endress+Hauser");

  const getCurrentTransmissionInterval = (
    currentTransmissionInterval: { id?: string; name?: string } | undefined,
  ) => {
    const productTransmissionIntervals = productRules.get(
      "transmissionIntervalOptions",
    );
    if (currentTransmissionInterval && productTransmissionIntervals) {
      return (
        productTransmissionIntervals.find(
          (ti: { id?: string; name?: string }) =>
            ti.name === currentTransmissionInterval.name &&
            ti.id === currentTransmissionInterval.id,
        ) ?? productRules.get("defaultTransmissionInterval", measuringInterval)
      );
    }
    return productRules.get("defaultTransmissionInterval", measuringInterval);
  };

  React.useEffect(() => {
    validateForm();
  }, [gpsError]);

  React.useEffect(() => {
    const sync = async () => {
      if (
        product &&
        !product.new &&
        manufacturer &&
        manufacturer?.id !== product?.manufacturerId
      ) {
        setFieldValue("product", undefined);
      }
      if (
        manufacturer?.new &&
        !tenant &&
        (userAdminTenants as $TSFixMe)?.length === 1
      ) {
        setFieldValue("tenant", userAdminTenants[0]);
      }
      if (
        product?.new &&
        manufacturer &&
        !manufacturer.new &&
        !(userAdminTenants as $TSFixMe)?.find(
          (t: $TSFixMe) => t.id === manufacturer.tenantId,
        )
      ) {
        setFieldValue("manufacturer", {
          name: manufacturer.name,
          new: true,
        });
      }
      if (product && !product.new) {
        if (showMeasuringInterval && !measuringInterval) {
          setFieldValue(
            "measuringInterval",
            productRules.get("defaultMeasuringInterval", intl),
          );
        }
        if (showTransmissionInterval && !showMeasuringInterval) {
          setFieldValue("measuringInterval", undefined);
          setFieldValue(
            "transmissionInterval",
            getCurrentTransmissionInterval(values.transmissionInterval),
          );
          setFieldValue("firstMeasurement", undefined);
        }
        if (showTimeOfFirstMeasurement && !firstMeasurement) {
          setFieldValue("firstMeasurement", new Date());
        }
      }
    };
    sync();
  }, [manufacturer, product]);

  React.useEffect(() => {
    const sync = async () => {
      // Applicable only for FWR30 variants
      if (measuringInterval && showMeasuringInterval) {
        if (
          transmissionInterval &&
          measuringInterval.transmissionOptions?.find(
            (o) => o.name === transmissionInterval.name,
          )
        ) {
          setFieldValue(
            "transmissionInterval",
            measuringInterval.transmissionOptions.find(
              (o) => o.name === transmissionInterval.name,
            ),
          );
        } else if (
          productRules.get("defaultMeasuringInterval", intl) &&
          measuringInterval.id ===
            productRules.get("defaultMeasuringInterval", intl).id
        ) {
          setFieldValue(
            "transmissionInterval",
            productRules.get("defaultTransmissionInterval", measuringInterval),
          );
        } else {
          setFieldValue(
            "transmissionInterval",
            last(measuringInterval.transmissionOptions),
          );
        }
      }
    };
    sync();
  }, [measuringInterval]);

  React.useEffect(() => {
    const sync = async () => {
      if (
        transmissionInterval &&
        showTransmissionInterval &&
        !showMeasuringInterval
      ) {
        setFieldValue(
          "transmissionInterval",
          getCurrentTransmissionInterval(values.transmissionInterval),
        );
      }
      if (gpsEnabled) {
        if (
          gpsInterval &&
          gpsIntervalOptions?.find((o) => o.name === gpsInterval.name)
        ) {
          setFieldValue(
            "gpsInterval",
            gpsIntervalOptions?.find((o) => o.name === gpsInterval.name),
          );
        } else if (
          gpsInterval &&
          gpsIntervalOptions &&
          gpsIntervalOptions.length &&
          gpsIntervalOptions?.find((o) => o.name === gpsInterval?.name) ===
            undefined
        ) {
          // set GPS not in range error
          setFieldValue("gpsInterval", null);
          setFieldTouched("gpsInterval", true);
          // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          loadGPSErrors("validation.gps_interval.not_in_range");
        } else if (!gpsIntervalOptions || gpsIntervalOptions.length === 0) {
          // set less than one hour error
          setFieldValue("gpsInterval", null);
          setFieldValue("gpsActive", false);
          setFieldTouched("gpsInterval", true);
          // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          loadGPSErrors("validation.gps_interval.less");
        } else {
          // clear errors for initial loading
          setFieldValue("gpsInterval", null);
          // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          loadGPSErrors(undefined);
        }
      }
    };
    sync();
  }, [transmissionInterval, gpsEnabled]);

  React.useEffect(() => {
    const sync = async () => {
      // sets GPS interval error message for switch button changes
      if (!gpsIntervalOptions && gpsEnabled) {
        setFieldValue("gpsInterval", null);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        loadGPSErrors("validation.gps_interval.less");
      }
      setFieldTouched("gpsInterval", true);
    };
    sync();
  }, [gpsActive]);

  const setExtendedOrderCode = (extendedOrderCode: $TSFixMe) => {
    if (extendedOrderCode) {
      setFieldValue("extendedOrderCode", extendedOrderCode);
    } else {
      setFieldValue("extendedOrderCode", null);
    }
  };

  // react to changes in order code in form
  React.useEffect(() => {
    const sync = async () => {
      if (orderCode) {
        try {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          const order_code = `${values.product.code}-${values.orderCode}`;
          const response = await api.get(
            "/endress/extended_order_code_lookup",
            { order_code },
          );
          const extendedOrderCode = response.extended_order_code
            ? extractCode(response.extended_order_code)
            : null;
          setExtendedOrderCode(extendedOrderCode);
        } catch {
          setExtendedOrderCode(null);
        }
      }
    };
    sync();
  }, [orderCode]);

  const initialization = React.useRef(skipFirstProductLookup);
  React.useEffect(() => {
    // skip lookup on first call from asset edit
    if (manufacturer && initialization.current) {
      initialization.current = false;
      return;
    }

    const sync = async () => {
      if (isNotEndress) {
        return;
      }
      if (couldBeAnEndressHauserSerialNumber(serial_number)) {
        try {
          const lookupResponse = await api.get("/endress/product_lookup", {
            serial_number,
            include: "order_code",
          });
          const extendedOrderCode = lookupResponse.extended_order_code
            ? extractCode(lookupResponse.extended_order_code)
            : undefined;
          const orderCodeValue = lookupResponse.order_code
            ? extractCode(lookupResponse.order_code)
            : undefined;
          const response = await api.get(`/products/${lookupResponse.id}`, {
            include: "tenant",
          });
          setFieldValue("manufacturer", {
            id: response.manufacturer.id,
            name: response.tenant.name,
            tenantId: response.tenant.id,
            tenantPublic: response.tenant.public,
            tenantName: response.tenant.name,
          });
          setFieldValue("product", {
            id: response.id,
            name: response.name,
            code: response.product_code,
            manufacturerId: response.manufacturer.id,
            tenantId: response.tenant.id,
            tenantPublic: response.tenant.public,
            tenantName: response.tenant.name,
          });
          if (orderCodeValue) {
            setFieldValue("orderCode", orderCodeValue);
          }
          setExtendedOrderCode(extendedOrderCode);
        } catch {
          // ok
        }
      }
    };

    sync();
  }, [serial_number]);

  return null;
}

AssetFormSynchronization.defaultProps = defaultProps;

export default withApi(AssetFormSynchronization);
